import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import SearchFormTextField from 'components/SearchV2/Components/SearchFormField/SearchFormTextField'
import React, { ComponentProps, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import RecentSearchSuggestions from '../SearchForm/SearchLocationInput/RecentSearchSuggestions'
import SearchMenuListItem from '../SearchForm/SearchMenu/SearchMenuListItem'
import SearchPlaceSelectList from '../SearchPlaceSelect/SearchPlaceSelectList'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import { rem } from 'polished'
import Caption from 'LuxKit/Typography/Caption'
import { take } from 'lib/array/arrayUtils'
import { TypeaheadActionType } from 'analytics/snowplow/events'
import SearchFormMultiLineTextField from 'components/SearchV2/Components/SearchFormField/SearchFormMultiLineTextField'
import SearchTextListItem from '../SearchTextListItem/SearchTextListItem'
import VerticalSpacer from '../../Common/Spacing/VerticalSpacer'

const NUMBER_OF_POPULAR_PLACES_DISPLAYED = 9
const NUMBER_OF_POPULAR_PLACES_DISPLAYED_FOR_NATURAL_LANGUAGE = 12

const PlaceControl = styled.div`
  width: 100%;

  &:not(.full-width) {
    position: relative;
  }
`

const SearchMenuListItemContainer = styled.div`
  width: ${rem(325)};
  display: flex;
  column-gap: ${rem(40)};
  flex-wrap: wrap;

  &.horizontal {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    width: 100%;
  }
`

const SearchItemContainer = styled.div`
  display: flex;
  column-gap: ${rem(40)};
  flex-wrap: wrap;
`

const RecentSearchSuggestionsContainer = styled.div`
  margin-bottom:  ${rem(28)};
`

const StyledCaption = styled(Caption)`
  padding: 0;
`
const StyledSuggestedSearchesHeaderCaption = styled(Caption)`
  margin-bottom: ${rem(8)};
`

interface Props {
  /**
   * @default fill-anchor
   */
  size?: ComponentProps<typeof DropdownList>['size']
  className?: string
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  value?: App.SearchItem
  required?: boolean
  label: string
  placeholder: string
  localInputRef: React.RefObject<HTMLInputElement>
  searchPhrase: string
  onSearchPhraseChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  handleOpen: (e: any) => void
  onClearClick: () => void
  finalOpen: boolean
  closeDropdown: () => void
  showPlaceholder: boolean
  recentSearches: Array<App.FullRecentSearch>
  selectedDropdown: number | undefined
  onRecentSearchDelete: (item: App.FullRecentSearch) => void
  onRecentSearchClick: (item: App.FullRecentSearch) => void
  placeholderTitle?: string
  placeholdersSearches: Array<App.SearchItem>
  onLocationSelect: (
    searchItem?: App.SearchItem,
    mouseEvent?: React.MouseEvent<HTMLElement>,
    actionType?: TypeaheadActionType,
  ) => void
  handleResultsChange: (results: Array<App.SearchItem>, searchPhrase: string, shouldTrack?: boolean, loading?: boolean) => void
  placeTypes?: Array<App.SearchPlaceType>
  searchTypes?: Array<App.SearchType>
  searchVertical?: App.SearchPlaceVertical
  overrideSearchItems?: Array<App.SearchItem>
  controlsContainerRef?: React.RefObject<HTMLDivElement>
  showNaturalLanguageSearchSuggestions?: boolean
}

function HotelSearchPlaceSelectDropdown(props: Props) {
  const {
    className,
    onKeyDown,
    value,
    required,
    label,
    placeholder,
    localInputRef,
    searchPhrase,
    onSearchPhraseChange,
    handleOpen,
    onClearClick,
    finalOpen,
    closeDropdown,
    showPlaceholder,
    recentSearches,
    selectedDropdown,
    onRecentSearchDelete,
    onRecentSearchClick,
    placeholderTitle,
    placeholdersSearches,
    onLocationSelect,
    handleResultsChange,
    placeTypes,
    searchTypes,
    searchVertical,
    overrideSearchItems,
    controlsContainerRef,
    showNaturalLanguageSearchSuggestions = false,
  } = props

  const placeHolderItems = take(
    placeholdersSearches,
    placeholdersSearches.some(item => item.searchType === 'naturalLanguage')
      ? NUMBER_OF_POPULAR_PLACES_DISPLAYED_FOR_NATURAL_LANGUAGE
      : NUMBER_OF_POPULAR_PLACES_DISPLAYED
  )

  const handleOnClick = useCallback((searchItem?: App.SearchItem) => {
    if (!searchItem) {
      onLocationSelect(searchItem)
      return
    }

    const itemIndex = placeHolderItems.findIndex(item => item.value === searchItem.value)
    if (itemIndex === -1) {
      onLocationSelect(searchItem)
      return
    }

    onLocationSelect(searchItem, undefined, undefined)
  }, [onLocationSelect, placeHolderItems])

  const naturalLanguageResults = useMemo(() => {
    return placeHolderItems.filter(item => item.searchType === 'naturalLanguage')
  }, [placeHolderItems])

  const nonNaturalLanguageResults = useMemo(() => {
    return placeHolderItems.filter(item => item.searchType !== 'naturalLanguage')
  }, [placeHolderItems])

  return (
    <PlaceControl className={className} onKeyDown={onKeyDown}>
      <HiddenInput
        required={required}
        value={value?.format.mainText}
      />
      {!showNaturalLanguageSearchSuggestions && <SearchFormTextField
        label={label}
        placeholder={placeholder}
        ref={localInputRef}
        value={searchPhrase}
        displayValue={value?.format.mainText ?? ''}
        onChange={onSearchPhraseChange}
        onFocus={handleOpen}
        onClear={onClearClick}
      />}
      {showNaturalLanguageSearchSuggestions && <SearchFormMultiLineTextField
        label={label}
        placeholder={placeholder}
        value={searchPhrase}
        displayValue={value?.format.mainText ?? ''}
        onChange={onSearchPhraseChange}
        onFocus={handleOpen}
        onClear={onClearClick}
        ref={localInputRef}
      />}
      {controlsContainerRef && <DropdownSheet
        size="fill-anchor"
        anchorRef={controlsContainerRef}
        triggerRef={localInputRef}
        open={finalOpen}
        data-testid="place-select"
        onClose={closeDropdown}
      >
        {showPlaceholder &&
          <>
            {!!recentSearches.length && <RecentSearchSuggestionsContainer>
              <RecentSearchSuggestions
                recentSearches={recentSearches}
                onDelete={onRecentSearchDelete}
                selectedDropdown={selectedDropdown}
                iconBackground
                onRecentSearchClick={onRecentSearchClick}
                horizontal
              />
            </RecentSearchSuggestionsContainer>}
            <VerticalSpacer gap={24}>
              {showNaturalLanguageSearchSuggestions && naturalLanguageResults.length > 0 &&
                <div>
                  <StyledSuggestedSearchesHeaderCaption variant="large" weight="bold">
                    Suggested searches
                  </StyledSuggestedSearchesHeaderCaption>
                  {naturalLanguageResults.map(
                    (suggestion, index) =>
                      <SearchTextListItem
                        key={`${suggestion.value}_${index}`}
                        text={suggestion.value}
                        onClick={handleOnClick}
                      />
                  )}
                </div>}
              <div>
                {placeholderTitle && <StyledCaption variant="large" weight="bold">{placeholderTitle}</StyledCaption>}
                <SearchItemContainer>
                  {nonNaturalLanguageResults.map((item, index) => (
                    <SearchMenuListItemContainer key={item.value}>
                      <SearchMenuListItem
                        key={item.value}
                        searchItem={item}
                        onClick={handleOnClick}
                        selected={index + recentSearches.length === selectedDropdown}
                        horizontal
                        searchVertical={searchVertical}
                        imageId={item.imageId}
                        useImage
                      />
                    </SearchMenuListItemContainer>))}
                </SearchItemContainer>
              </div>
            </VerticalSpacer>
          </>}
        {!showPlaceholder &&
          <SearchPlaceSelectList
            searchPhrase={searchPhrase}
            onResultsChange={handleResultsChange}
            onChange={onLocationSelect}
            placeTypes={placeTypes}
            searchTypes={searchTypes}
            vertical={searchVertical}
            selectedDropdown={selectedDropdown}
            overrideSearchItems={overrideSearchItems}
            showHorizontalLayout
            useImage
            showNaturalLanguageSearchSuggestions={showNaturalLanguageSearchSuggestions}
          />}
      </DropdownSheet>}
    </PlaceControl>
  )
}

export default HotelSearchPlaceSelectDropdown
